/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { NgModule } from '@angular/core';
import { RouterModule } from '@angular/router';
import {
  APPLICATION_VIEW_OFFICE, BuilderPermission, BuilderPermissionSet,
  SCHEDULE_TYPE_CONSTRUCTION,
  SCHEDULE_TYPE_MAINTENANCE,
  SCHEDULE_TYPE_PRE_CONSTRUCTION,
  SCHEDULE_TYPE_SALES,
} from '@gh/builder-shared-data';
import { createRoute } from '@gh/common-shared';
import { ConstructionScheduleListComponent } from './components/construction-schedule-list/construction-schedule-list.component';
import { ConstructionWorksheetComponent } from './components/construction-worksheet/construction-worksheet.component';
import { MaintenanceScheduleListComponent } from './components/maintenance-schedule-list/maintenance-schedule-list.component';
import { PreConstructionScheduleListComponent } from './components/pre-construction-schedule-list/pre-construction-schedule-list.component';
import { PreConstructionWorksheetComponent } from './components/pre-construction-worksheet/pre-construction-worksheet.component';
import { SalesScheduleListComponent } from './components/sales-schedule-list/sales-schedule-list.component';
import { ScheduleCombinedGanttDetailsComponent } from './components/schedule-combined-gantt-details/schedule-combined-gantt-details.component';
import { ConstructionScheduleDetailsComponent } from './components/schedule-details/construction-schedule-details/construction-schedule-details.component';
import { PreMainScheduleDetailsComponent } from './components/schedule-details/pre-main-schedule-details/pre-main-schedule-details.component';
import { SalesScheduleDetailsComponent } from './components/schedule-details/sales-schedule-details/sales-schedule-details.component';
import { ScheduleGanttDetailsComponent } from './components/schedule-gantt-details/schedule-gantt-details.component';
import { ScheduleTaskDetailsComponent } from './components/schedule-task-details/schedule-task-details.component';
import { ConstructionWorksheetBusinessService } from './resources/construction-worksheet-business.service';
import { PreConstructionWorksheetBusinessService } from './resources/pre-construction-worksheet-business.service';
import { ScheduleCombinedGanttDetailsBusinessService } from './resources/schedule-combined-gantt-details-business.service';
import { ScheduleDetailsBusinessService } from './resources/schedule-details-business.service';
import { ScheduleGanttDetailsBusinessService } from './resources/schedule-gantt-details-business.service';
import { ScheduleListBusinessService } from './resources/schedule-list-business.service';
import { ScheduleTaskDetailsBusinessService } from './resources/schedule-task-details-business.service';

const routes = [
  createRoute({
    path: 'construction',
    component: ConstructionScheduleListComponent,
    init: {
      service: ScheduleListBusinessService,
      selector: 'initConstructionScheduleListView',
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Schedules.Construction.LIST',
    },
  }),
  createRoute({
    path: 'pre-construction',
    component: PreConstructionScheduleListComponent,
    init: {
      service: ScheduleListBusinessService,
      selector: 'initPreConstructionScheduleListView',
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Schedules.PreConstruction.LIST',
    },
  }),
  createRoute({
    path: 'maintenance',
    component: MaintenanceScheduleListComponent,
    init: {
      service: ScheduleListBusinessService,
      selector: 'initMaintenanceScheduleListView',
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Schedules.Maintenance.LIST',
    },
  }),
  createRoute({
    path: 'sales',
    component: SalesScheduleListComponent,
    init: {
      service: ScheduleListBusinessService,
      selector: 'initSalesScheduleListView',
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Schedules.Sales.LIST',
    },
  }),
  createRoute({
    path: 'construction/worksheet',
    component: ConstructionWorksheetComponent,
    init: ConstructionWorksheetBusinessService,
    leave: true,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Schedules.Construction.EDIT_VIEW',
    },
  }),
  createRoute({
    path: 'pre-construction/worksheet',
    component: PreConstructionWorksheetComponent,
    init: {
      service: PreConstructionWorksheetBusinessService,
      selector: 'initView',
      noWaiting: true,
    },
    leave: true,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Schedules.PreConstruction.EDIT_VIEW',
    },
  }),
  createRoute({
    path: 'construction/new',
    component: ConstructionScheduleDetailsComponent,
    init: ScheduleDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Schedules.Construction.CREATE',
    },
    static: {
      type: SCHEDULE_TYPE_CONSTRUCTION,
    },
  }),
  createRoute({
    path: 'construction/:scheduleId',
    component: ConstructionScheduleDetailsComponent,
    init: ScheduleDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Schedules.Construction.EDIT_VIEW',
    },
    static: {
      type: SCHEDULE_TYPE_CONSTRUCTION,
    },
  }),
  createRoute({
    path: 'pre-construction/new',
    component: PreMainScheduleDetailsComponent,
    init: ScheduleDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Schedules.PreConstruction.CREATE',
    },
    static: {
      type: SCHEDULE_TYPE_PRE_CONSTRUCTION,
    },
  }),
  createRoute({
    path: 'pre-construction/:scheduleId',
    component: PreMainScheduleDetailsComponent,
    init: ScheduleDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Schedules.PreConstruction.EDIT_VIEW',
    },
    static: {
      type: SCHEDULE_TYPE_PRE_CONSTRUCTION,
    },
  }),
  createRoute({
    path: 'maintenance/new',
    component: PreMainScheduleDetailsComponent,
    init: ScheduleDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Schedules.Maintenance.CREATE',
    },
    static: {
      type: SCHEDULE_TYPE_MAINTENANCE,
    },
  }),
  createRoute({
    path: 'maintenance/:scheduleId',
    component: PreMainScheduleDetailsComponent,
    init: ScheduleDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Schedules.Maintenance.EDIT_VIEW',
    },
    static: {
      type: SCHEDULE_TYPE_MAINTENANCE,
    },
  }),
  createRoute({
    path: 'sales/new',
    component: SalesScheduleDetailsComponent,
    init: ScheduleDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Schedules.Sales.CREATE',
    },
    static: {
      type: SCHEDULE_TYPE_SALES,
    },
  }),
  createRoute({
    path: 'sales/:scheduleId',
    component: SalesScheduleDetailsComponent,
    init: ScheduleDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Schedules.Sales.EDIT_VIEW',
    },
    static: {
      type: SCHEDULE_TYPE_SALES,
    },
  }),
  createRoute({
    path: 'construction/combined/gantt-chart',
    component: ScheduleCombinedGanttDetailsComponent,
    init: {
      service: ScheduleCombinedGanttDetailsBusinessService,
      selector: 'initView',
    },
    static: {
      type: SCHEDULE_TYPE_CONSTRUCTION,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: ['Schedules.Construction.EDIT_VIEW'],
    },
  }),
  createRoute({
    path: 'pre-construction/combined/gantt-chart',
    component: ScheduleCombinedGanttDetailsComponent,
    init: {
      service: ScheduleCombinedGanttDetailsBusinessService,
      selector: 'initView',
    },
    static: {
      type: SCHEDULE_TYPE_PRE_CONSTRUCTION,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: ['Schedules.PreConstruction.EDIT_VIEW'],
    },
  }),
  createRoute({
    path: 'maintenance/combined/gantt-chart',
    component: ScheduleCombinedGanttDetailsComponent,
    init: {
      service: ScheduleCombinedGanttDetailsBusinessService,
      selector: 'initView',
    },
    static: {
      type: SCHEDULE_TYPE_MAINTENANCE,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: ['Schedules.Maintenance.EDIT_VIEW'],
    },
  }),
  createRoute({
    path: 'sales/combined/gantt-chart',
    component: ScheduleCombinedGanttDetailsComponent,
    init: {
      service: ScheduleCombinedGanttDetailsBusinessService,
      selector: 'initView',
    },
    static: {
      type: SCHEDULE_TYPE_SALES,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: ['Schedules.Sales.EDIT_VIEW'],
    },
  }),
  createRoute({
    path: 'construction/:scheduleId/gantt-chart',
    component: ScheduleGanttDetailsComponent,
    leave: true,
    init: {
      service: ScheduleGanttDetailsBusinessService,
      selector: 'initView',
    },
    static: {
      type: SCHEDULE_TYPE_CONSTRUCTION,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Schedules.Construction.EDIT',
        'Schedules.Construction.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'pre-construction/:scheduleId/gantt-chart',
    component: ScheduleGanttDetailsComponent,
    leave: true,
    init: {
      service: ScheduleGanttDetailsBusinessService,
      selector: 'initView',
    },
    static: {
      type: SCHEDULE_TYPE_PRE_CONSTRUCTION,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Schedules.PreConstruction.EDIT',
        'Schedules.PreConstruction.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'maintenance/:scheduleId/gantt-chart',
    component: ScheduleGanttDetailsComponent,
    leave: true,
    init: {
      service: ScheduleGanttDetailsBusinessService,
      selector: 'initView',
    },
    static: {
      type: SCHEDULE_TYPE_MAINTENANCE,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Schedules.Maintenance.EDIT',
        'Schedules.Maintenance.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'sales/:scheduleId/gantt-chart',
    component: ScheduleGanttDetailsComponent,
    leave: true,
    init: {
      service: ScheduleGanttDetailsBusinessService,
      selector: 'initView',
    },
    static: {
      type: SCHEDULE_TYPE_SALES,
    },
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: [
        'Schedules.Sales.EDIT',
        'Schedules.Sales.VIEW',
      ],
    },
  }),
  createRoute({
    path: 'construction/:scheduleId/task/:taskId',
    component: ScheduleTaskDetailsComponent,
    init: ScheduleTaskDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Schedules.Construction.EDIT_VIEW',
    },
    static: {
      type: SCHEDULE_TYPE_CONSTRUCTION,
    },
  }),
  createRoute({
    path: 'sales/:scheduleId/task/:taskId',
    component: ScheduleTaskDetailsComponent,
    init: ScheduleTaskDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Schedules.Sales.EDIT_VIEW',
    },
    static: {
      type: SCHEDULE_TYPE_SALES,
    },
  }),
  createRoute({
    path: 'pre-construction/:scheduleId/task/:taskId',
    component: ScheduleTaskDetailsComponent,
    init: ScheduleTaskDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Schedules.PreConstruction.EDIT_VIEW',
    },
    static: {
      type: SCHEDULE_TYPE_PRE_CONSTRUCTION,
    },
  }),
  createRoute({
    path: 'maintenance/:scheduleId/task/:taskId',
    component: ScheduleTaskDetailsComponent,
    init: ScheduleTaskDetailsBusinessService,
    leave: true,
    auth: {
      applicationView: APPLICATION_VIEW_OFFICE,
      permission: 'Schedules.Maintenance.EDIT_VIEW',
    },
    static: {
      type: SCHEDULE_TYPE_MAINTENANCE,
    },
  }),
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  declarations: [],
  exports: [RouterModule],
})
export class BuilderSchedulingRoutingModule {
}
